import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload } from '@aws-sdk/lib-storage';
import { v4 as uuidv4 } from 'uuid';
import { config } from './config';
import { useAuth } from './auth';
import { getS3 } from './aws';
import { buildSourceKey, createUploadRow, deleteUploadRow, setUploadStatus } from './uploads';
import { isSupportedExtension, supportedExtensionsHumanList } from './extension-validator';
import { Upload as UploadRow } from './types';

const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024;

interface InFlight {
  uploadId: string;
  filename: string;
  abort: () => Promise<void>;
}

export function UploadPage() {
  const { identityId } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progressBytes, setProgressBytes] = useState<number>(0);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done' | 'failed'>('idle');
  const [dragOver, setDragOver] = useState(false);
  const inFlight = useRef<InFlight | null>(null);

  const onSelect = useCallback((f: File | null) => {
    setError(null);
    setStatus('idle');
    setProgressBytes(0);
    if (!f) {
      setFile(null);
      return;
    }
    if (!isSupportedExtension(f.name)) {
      setError(`Unsupported file type. Supported: ${supportedExtensionsHumanList()}.`);
      setFile(null);
      return;
    }
    setFile(f);
  }, []);

  const start = async () => {
    if (!file || !identityId) return;
    setError(null);
    setStatus('uploading');
    setProgressBytes(0);
    const uploadId = uuidv4();
    const srcKey = buildSourceKey(identityId, uploadId, file.name);
    const now = new Date().toISOString();
    const row: UploadRow = {
      userId: identityId,
      uploadId,
      srcBucket: config.sourceBucket,
      srcKey,
      filename: file.name,
      sizeBytes: file.size,
      createdAt: now,
      updatedAt: now,
      status: 'uploading',
    };

    try {
      await createUploadRow(row);
    } catch (e) {
      setError(`Could not create upload record: ${e}`);
      setStatus('failed');
      return;
    }

    const partSize = Math.max(5 * 1024 * 1024, Math.ceil(file.size / 1000));
    const upload = new Upload({
      client: getS3(),
      params: { Bucket: config.sourceBucket, Key: srcKey, Body: file, ContentType: file.type },
      queueSize: 4,
      partSize: file.size > MULTIPART_THRESHOLD_BYTES ? partSize : undefined,
      leavePartsOnError: false,
    });
    upload.on('httpUploadProgress', (p) => setProgressBytes(p.loaded ?? 0));
    inFlight.current = {
      uploadId,
      filename: file.name,
      abort: () => upload.abort(),
    };

    try {
      await upload.done();
      await setUploadStatus(identityId, uploadId, 'submitted');
      setStatus('done');
      inFlight.current = null;
      navigate(`/library/${uploadId}`);
    } catch (e) {
      setStatus('failed');
      setError(`Upload failed: ${e}`);
    }
  };

  const cancel = async () => {
    const f = inFlight.current;
    if (!f || !identityId) return;
    try {
      await f.abort();
    } catch {
      // Best-effort abort — proceed to delete the record either way.
    }
    try {
      await deleteUploadRow(identityId, f.uploadId);
    } catch {
      // If the row is already gone, that's fine.
    }
    inFlight.current = null;
    setStatus('idle');
    setProgressBytes(0);
    setFile(null);
  };

  const retry = async () => {
    if (!file) return;
    // Best-effort cleanup of any prior in-flight + row, then start fresh.
    await cancel();
    await start();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onSelect(f);
  };

  return (
    <div>
      <h1>Upload a video</h1>
      <div
        className="card"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: dragOver ? '2px dashed var(--color-accent)' : '1px dashed var(--color-border)',
          padding: 'var(--space-5)',
          textAlign: 'center',
        }}
      >
        <p className="muted">Drag a video file here, or pick one below.</p>
        <input
          type="file"
          accept="video/*"
          onChange={(e) => onSelect(e.target.files?.[0] ?? null)}
          disabled={status === 'uploading'}
        />
        {file && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <div>
              <strong>{file.name}</strong>{' '}
              <span className="muted">({(file.size / (1024 * 1024)).toFixed(1)} MB)</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="error" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </p>
      )}

      {status === 'uploading' && file && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <progress max={file.size} value={progressBytes} style={{ width: '100%' }} />
          <p className="muted">
            {(progressBytes / (1024 * 1024)).toFixed(1)} MB / {(file.size / (1024 * 1024)).toFixed(1)}{' '}
            MB
          </p>
          <button className="secondary" onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {status === 'idle' && file && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <button onClick={start} disabled={!identityId}>
            Start upload
          </button>
        </div>
      )}

      {status === 'failed' && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <button onClick={retry}>Retry</button>
          <button className="secondary" onClick={() => setFile(null)} style={{ marginLeft: 8 }}>
            Choose a different file
          </button>
        </div>
      )}
    </div>
  );
}
